import React, { useState } from 'react';
import { QrCode, Loader2 } from 'lucide-react';
import jsPDF from 'jspdf';

interface QRPDFButtonProps {
    number: string;
    name: string;
    fecha_visita: string;
    className?: string;
    children?: React.ReactNode;
    variant?: 'primary' | 'secondary' | 'outline';
    size?: 'sm' | 'md' | 'lg';
}

const QrButton: React.FC<QRPDFButtonProps> = ({
    number,
    name,
    fecha_visita,
    children
}) => {
    const [isGenerating, setIsGenerating] = useState(false);

    const loadImageAsBase64 = async (imageUrl: string): Promise<string> => {
        const response = await fetch(imageUrl);
        const blob = await response.blob();

        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
        });
    };

    const formatDateTime = (isoString: string): string => {
        const date = new Date(isoString);
        return new Intl.DateTimeFormat('es-EC', {
            timeZone: 'America/Guayaquil',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        }).format(date).replace(/(\d{2})\/(\d{2})\/(\d{4}), (\d{2}):(\d{2}):(\d{2})/, '$3-$2-$1 $4:$5:$6');
    };

    // Función para generar QR usando API pública
    const generateQRCode = async (data: string): Promise<string> => {
        const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(data)}`;

        try {
            const response = await fetch(qrApiUrl);
            const blob = await response.blob();

            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Error generating QR:', error);
            throw new Error('Failed to generate QR code');
        }
    };

    // Función para generar y descargar PDF usando jsPDF
    const generatePDF = async (qrImageData: string, name: string, fecha_visita: string) => {
        // Importar jsPDF dinámicamente
        const fechaFormateada = formatDateTime(fecha_visita);
        return new Promise<void>((resolve, reject) => {
            try {
                // Crear nuevo documento PDF (A4)
                const doc = new (jsPDF as any)({
                    orientation: 'portrait',
                    unit: 'mm',
                    format: 'a4'
                });

                // Configuración de la página
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();

                // Título principal
                doc.setFontSize(24);
                doc.setFont('helvetica', 'bold');
                doc.text(`Código QR de Acceso`, pageWidth / 2, 45, { align: 'center' });

                // Información adicional
                doc.setFontSize(14);
                doc.setFont('helvetica', 'normal');
                doc.text(`Nombre: ${name}`, pageWidth / 2, 60, { align: 'center' });
                doc.text(`Fecha de visita: ${fechaFormateada}`, pageWidth / 2, 65, { align: 'center' });

                // Cargar imagen QR y agregarla al PDF
                const qrImage = new Image();
                qrImage.crossOrigin = 'anonymous';

                qrImage.onload = async () => {
                    try {
                        // Calcular posición centrada para el QR
                        const logoUrl = '/santander_logo.png'; // Cambia por tu URL
                        const logoBase64 = await loadImageAsBase64(logoUrl);

                        // Agregar logo en el encabezado
                        const logoWidth = 80;
                        const logoHeight = 25;
                        const logoX = (pageWidth - logoWidth) / 2;
                        const logoY = 10;

                        doc.addImage(logoBase64, 'PNG', logoX, logoY, logoWidth, logoHeight);

                        const qrSize = 170; // 80mm
                        const qrX = (pageWidth - qrSize) / 2;
                        const qrY = 80;

                        // Agregar imagen QR al PDF
                        doc.addImage(qrImageData, 'PNG', qrX, qrY, qrSize, qrSize);

                        // Instrucciones
                        doc.setFontSize(12);
                        doc.setTextColor(100, 100, 100);
                        doc.text('Escanea este código QR en la entrada/salida de las instalaciones', pageWidth / 2, qrY + qrSize + 20, { align: 'center' });

                        // Información adicional en la parte inferior
                        doc.setFontSize(10);
                        doc.text(`Este código QR es de uso exclusivo para la persona que se generó la visita`, pageWidth / 2, pageHeight - 20, { align: 'center' });

                        // Generar nombre del archivo
                        const fileName = `QR_${name}_${new Date().toISOString().split('T')[0]}.pdf`;

                        // Descargar PDF
                        doc.save(fileName);

                        resolve();
                    } catch (error) {
                        reject(error);
                    }
                };

                qrImage.onerror = () => reject(new Error('Could not load QR image'));
                qrImage.src = qrImageData;

            } catch (error) {
                reject(error);
            }
        });
    };

    const handleGenerateQRPDF = async () => {
        if (!number) {
            alert('Número requerido para generar QR');
            return;
        }
        if (!name) {
            alert('Nombre requerido para generar QR');
            return;
        }
        setIsGenerating(true);
        try {
            const qrImageData = await generateQRCode(number.toString());
            await generatePDF(qrImageData, name, fecha_visita);
        } catch (error) {
            console.error('Error generating QR PDF:', error);
            alert('Error al generar el PDF con QR. Por favor intenta nuevamente.');
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <button
            onClick={handleGenerateQRPDF}
            disabled={isGenerating || !number}
            className={`p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20 rounded-lg transition-colors"`}
            title={`Generar QR`}
        >
            {isGenerating ? (
                <>
                    <Loader2 className={`w-4 h-4 animate-spin`} />
                </>
            ) : (
                <>
                    {children ? (
                        children
                    ) : (
                        <>
                            <QrCode className={`w-4 h-4 transition-colors"`} />
                        </>
                    )}
                </>
            )}
        </button>
    );
};

export default QrButton;